/**
 * The revision bar: the project's saved snapshots newest-first, the
 * live chip, and Save revision. Clicking a revision points the viewer
 * at its (immutable) snapshot corpus; Live returns to the working
 * project. Each chip carries the open-findings count at save time --
 * the compliance history at a glance.
 */
import { ACCENT, INK, tint } from '../../../theme';
import { VERDICT } from '../../../strata';
import type { Revision } from '../revisions';

const chipStyle = (active: boolean) => ({
  fontSize: '12px',
  padding: '0.2rem 0.6rem',
  borderRadius: '4px',
  border: `1px solid ${active ? ACCENT.blue : INK.faint}`,
  background: active ? tint(ACCENT.blue, 0.2) : 'transparent',
  color: active ? ACCENT.blue : INK.primary,
  cursor: 'pointer',
});

export function RevisionBar({
  revisions,
  viewing,
  saving,
  onSave,
  onView,
}: {
  revisions: Revision[];
  viewing: Revision | null;
  saving: boolean;
  onSave: () => void;
  onView: (revision: Revision | null) => void;
}) {
  return (
    <div
      className="ui-overlay glass-panel"
      style={{
        top: 'auto', bottom: '1rem', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.5rem 0.9rem', maxWidth: '70vw', overflowX: 'auto',
      }}
    >
      <span style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: INK.secondary }}>
        Revisions
      </span>
      <button onClick={() => onView(null)} style={chipStyle(viewing === null)}>
        ● Live
      </button>
      {revisions.map((revision) => (
        <button
          key={revision.id}
          onClick={() => onView(revision)}
          style={chipStyle(viewing?.id === revision.id)}
          title={`${revision.element_count} elements · ${revision.open_findings} open findings${revision.label ? ` · ${revision.label}` : ''}`}
        >
          r{revision.number}
          <span
            style={{
              color: revision.open_findings > 0 ? VERDICT.violating : VERDICT.complying,
              marginLeft: '0.3rem',
            }}
          >
            {revision.open_findings > 0 ? revision.open_findings : '✓'}
          </span>
        </button>
      ))}
      <button
        disabled={saving}
        onClick={onSave}
        style={{
          fontSize: '12px', padding: '0.2rem 0.6rem', borderRadius: '4px',
          border: `1px solid ${ACCENT.green}`, background: tint(ACCENT.green, 0.15),
          color: ACCENT.green, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {saving ? 'Saving…' : 'Save revision'}
      </button>
    </div>
  );
}
